import React from 'react';
import memoize from 'memoize-one';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { type AISnippetRendererProps, type SnippetRendererProps } from '@atlaskit/link-provider';

import { isFedRamp } from '@atlassian/atl-context';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { AISnippetWithRelayEnvironment } from '@atlassian/ai-snippet';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent';
import { fg } from '@confluence/feature-gating';
import { SessionData } from '@confluence/session-data/entry-points/SessionData';
import { SessionDataContext } from '@confluence/session-data/entry-points/SessionDataContext';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useIsEligibleRovoAwareness } from '@confluence/rovo-awareness/entry-points/useIsEligibleRovoAwareness';

const emojiWrapperStyles = css({
	display: 'inline-block',
	font: token('font.body.small'),
	justifyContent: 'center',
	overflow: 'hidden',
	margin: 'auto',
	minWidth: '16px',
});

const SmartCardEmojiWrapper: React.ComponentType<React.HTMLAttributes<HTMLSpanElement>> = ({
	children,
}) => <span css={emojiWrapperStyles}>{children}</span>;

// Custom render wrapper for AISnippet providing session data
const AISnippetRenderer = (props: SnippetRendererProps) => {
	const { displayName, userId, isAdminHubAIEnabled } = useSessionData();
	const isEligible = useIsEligibleRovoAwareness();
	const shouldDisplayAwareness =
		isEligible && expValEquals('rovo_awareness_experiment', 'cohort', 'test');

	// Gate ON: environment-aware enablement via feature encoding; Gate OFF: legacy FedRAMP fallback
	const isAIFeatureEnabled = fg('use_frontend_feature_encoding_sdk')
		? isFeatureEnabled('confluence', 'confluence-ai')
		: !isFedRamp();

	if (
		!isAdminHubAIEnabled ||
		!isAIFeatureEnabled ||
		!expVal('confluence-ai-linking-ai-snippets', 'should_render_ai_snippet', false)
	) {
		return props.fallbackComponent;
	}

	return (
		<AISnippetWithRelayEnvironment
			{...(props as AISnippetRendererProps)}
			showAwareness={shouldDisplayAwareness}
			userId={userId ?? undefined}
			displayName={displayName ?? undefined}
		/>
	);
};

export const getSmartCardRenderers = memoize(() => ({
	renderers: {
		emoji: (emojiId, _parentComponent = 'SmartCard', contentType = 'page') => (
			<SessionData>
				{(sessionData) => (
					<SessionDataContext.Provider value={sessionData}>
						<LazyEmojiComponentLoader
							emoji={emojiId}
							height={16}
							context="smartCard"
							wrapper={SmartCardEmojiWrapper}
							contentType={contentType}
						/>
					</SessionDataContext.Provider>
				)}
			</SessionData>
		),
		snippet: fg('cc-ai-snippet-renderer-gate') ? AISnippetRenderer : undefined,
	},
}));
